import { useEffect, useRef, useState } from "react";
import { Header } from "../components/Header";
import { useDemoSettings } from "../contexts/demo-settings-context";
import { useAnalytics } from "../lib/analytics";
import { useClientTranslations } from "../lib/i18n/client";
import { callDigitalNurse } from "../services/digitalNurseService";
import styles from "./Communication.module.css";

/**
 * Placeholder clinic messages — the messaging backend is not wired up yet.
 * Every user-visible string is a communication-namespace key so the page
 * reads in the patient's own language; `hoursAgo` feeds the `timeAgo` plural.
 *
 * Senders carry a ROLE, not a name. These used to be hardcoded to one real
 * clinic ("Hadassah Fertility Center" / "Hadassah Laboratories") in all five
 * locales, which every other clinic's patients also saw. The clinic sender now
 * resolves to the configured organisation name when there is one, and falls
 * back to a neutral role; the lab sender is always neutral, since not every
 * provider on this product is a clinic with a laboratory.
 */
type SenderRole = "clinic" | "lab";

interface ChatMessage {
	id: string;
	sender: SenderRole;
	titleKey: string;
	contentKey: string;
	hoursAgo: number;
	isRead: boolean;
}

const MOCK_MESSAGES: ChatMessage[] = [
	{
		id: "mock-1",
		sender: "clinic",
		titleKey: "messages.items.appointmentConfirmation.title",
		contentKey: "messages.items.appointmentConfirmation.content",
		hoursAgo: 2,
		isRead: false,
	},
	{
		id: "mock-2",
		sender: "lab",
		titleKey: "messages.items.labResults.title",
		contentKey: "messages.items.labResults.content",
		hoursAgo: 5,
		isRead: true,
	},
	{
		id: "mock-3",
		sender: "clinic",
		titleKey: "messages.items.preAppointmentQuestionnaire.title",
		contentKey: "messages.items.preAppointmentQuestionnaire.content",
		hoursAgo: 5,
		isRead: true,
	},
];

export function Communication() {
	const { logCommunication } = useAnalytics();
	const { t } = useClientTranslations("communication");

	const [showNurseModal, setShowNurseModal] = useState(false);
	const [nurseFormData, setNurseFormData] = useState({
		name: "",
		phone: "",
	});
	const [isSubmittingNurse, setIsSubmittingNurse] = useState(false);
	const { organizationName } = useDemoSettings();

	// The clinic sender takes the configured organisation name when one is set;
	// otherwise a neutral role, never another clinic's brand.
	// typeof, not just a truthy check (Baz, #237): the settings come from an
	// untyped `res.json()`, so a truthy non-string would reach here and throw on
	// .trim(), taking down the whole Messages render instead of falling back.
	// demo-settings-context normalises at the boundary too; this is the guard
	// for any other caller.
	const clinicName =
		typeof organizationName === "string" ? organizationName.trim() : "";
	const senderName = (role: SenderRole): string =>
		role === "clinic"
			? clinicName || t("messages.senders.clinic")
			: t("messages.senders.lab");

	const hasTrackedPageView = useRef(false);
	useEffect(() => {
		if (!hasTrackedPageView.current) {
			hasTrackedPageView.current = true;
			logCommunication("page_viewed");
		}
	}, [logCommunication]);

	const handleNurseRequest = async () => {
		if (!nurseFormData.name || !nurseFormData.phone) {
			return;
		}

		setIsSubmittingNurse(true);
		try {
			const data = await callDigitalNurse({
				fullName: nurseFormData.name,
				phoneNumber: nurseFormData.phone,
				...(organizationName ? { clinicName: organizationName } : {}),
			});

			if (data.success) {
				logCommunication("digital_nurse_call_requested");
				setShowNurseModal(false);
				setNurseFormData({ name: "", phone: "" });
			}
		} catch (error) {
			console.error("Digital nurse call error:", error);
		} finally {
			setIsSubmittingNurse(false);
		}
	};

	const handleCloseModal = () => {
		setShowNurseModal(false);
		setNurseFormData({ name: "", phone: "" });
	};

	return (
		<div className={styles.wrapper}>
			<Header />
			<main className={styles.main}>
				<section className={styles.messagesSection}>
					<h2 className={styles.messagesHeading}>{t("messages.heading")}</h2>
					{MOCK_MESSAGES.length > 0 ? (
						<div className={styles.messagesList}>
							{MOCK_MESSAGES.map((message) => (
								<div
									key={message.id}
									className={`${styles.messageCard} ${
										message.isRead
											? styles.messageCardRead
											: styles.messageCardUnread
									}`}
								>
									<div className={styles.messageCardHeader}>
										<div className={styles.messageCardSenderRow}>
											<span
												className={`${styles.messageCardDot} ${
													message.isRead ? styles.dotRead : styles.dotUnread
												}`}
											/>
											<span
												className={
													message.isRead
														? styles.senderRead
														: styles.senderUnread
												}
											>
												{senderName(message.sender)}
											</span>
										</div>
										<span className={styles.messageCardTimestamp}>
											{t("messages.timeAgo", { count: message.hoursAgo })}
										</span>
									</div>
									<p
										className={
											message.isRead ? styles.titleRead : styles.titleUnread
										}
									>
										{t(message.titleKey)}
									</p>
									<p className={styles.messageCardContent}>
										{t(message.contentKey)}
									</p>
								</div>
							))}
						</div>
					) : (
						<div className={styles.messagesEmptyState}>
							<img
								src="/images/bottom-black-nav-communication.svg"
								alt=""
								className={styles.emptyStateIcon}
							/>
							<h3 className={styles.emptyStateTitle}>
								{t("emptyState.title")}
							</h3>
							<p className={styles.emptyStateDescription}>
								{t("emptyState.description")}
							</p>
						</div>
					)}
				</section>
			</main>

			<div className={styles.nurseButtonContainer}>
				<button
					type="button"
					className={styles.callNurseButton}
					onClick={() => setShowNurseModal(true)}
				>
					{t("digitalNurse.callButton")}
				</button>
			</div>

			{showNurseModal && (
				<div
					role="dialog"
					aria-modal="true"
					className={styles.modalOverlay}
					onClick={handleCloseModal}
					onKeyDown={(e) => {
						if (e.key === "Escape") handleCloseModal();
					}}
				>
					<div
						role="document"
						className={styles.modalContent}
						onClick={(e) => e.stopPropagation()}
						onKeyDown={() => {}}
					>
						<div className={styles.modalHeader}>
							<h3 className={styles.modalTitle}>
								{t("digitalNurse.modalTitle")}
							</h3>
							<button
								type="button"
								className={styles.modalCloseButton}
								onClick={handleCloseModal}
							>
								<svg
									width="20"
									height="20"
									viewBox="0 0 24 24"
									fill="none"
									stroke="currentColor"
									strokeWidth="2"
									strokeLinecap="round"
									strokeLinejoin="round"
									aria-label="Close"
									role="img"
								>
									<line x1="18" y1="6" x2="6" y2="18" />
									<line x1="6" y1="6" x2="18" y2="18" />
								</svg>
							</button>
						</div>

						<div className={styles.modalBody}>
							<div className={styles.formField}>
								<label htmlFor="nurse-name" className={styles.formLabel}>
									{t("digitalNurse.yourName")}
								</label>
								<input
									id="nurse-name"
									type="text"
									className={styles.formInput}
									value={nurseFormData.name}
									onChange={(e) =>
										setNurseFormData({
											...nurseFormData,
											name: e.target.value,
										})
									}
									placeholder={t("digitalNurse.enterName")}
								/>
							</div>
							<div className={styles.formField}>
								<label htmlFor="nurse-phone" className={styles.formLabel}>
									{t("digitalNurse.phoneNumber")}
								</label>
								<input
									id="nurse-phone"
									type="tel"
									className={styles.formInput}
									value={nurseFormData.phone}
									onChange={(e) =>
										setNurseFormData({
											...nurseFormData,
											phone: e.target.value,
										})
									}
									placeholder={t("digitalNurse.enterPhone")}
								/>
							</div>
						</div>

						<div className={styles.modalFooter}>
							<button
								type="button"
								className={styles.submitButton}
								onClick={handleNurseRequest}
								disabled={
									isSubmittingNurse ||
									!nurseFormData.name ||
									!nurseFormData.phone
								}
							>
								{isSubmittingNurse
									? t("digitalNurse.submitting")
									: t("digitalNurse.requestCall")}
							</button>
						</div>
					</div>
				</div>
			)}
		</div>
	);
}
